import React from 'react';
import styled from 'styled-components';
import TrueImpactText, { TrueImpactTextProps } from '../base/TrueImpactText';
import { IconDefinition, IconProp } from '@fortawesome/fontawesome-svg-core';
import { IconDefinition as IconDefinitionSolid } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SPACERS } from '../settings/variables/Spacers';
import { COLORS } from '../settings/variables/Colors';
import { spinAnimation } from '../../components/LoadingSpinner';

interface TrueImpactIconTextProps {
  gap?: string;
  margin?: string;
  padding?: string;
  isBold?: boolean;
  className?: string;
  isSpinner?: boolean;
  variant?: TrueImpactTextProps['variant'];
  color?: keyof typeof COLORS;
  hoverColor?: keyof typeof COLORS;
  label: string | JSX.Element;
  allowTextSelection?: TrueImpactTextProps['allowTextSelection'];
  icon: IconDefinition | IconDefinitionSolid | IconProp;
  onClick?: () => unknown;
}

const StyledDiv = styled.div<{
  gap: TrueImpactIconTextProps['gap'];
  color: TrueImpactIconTextProps['color'];
  margin: TrueImpactIconTextProps['margin'];
  padding: TrueImpactIconTextProps['padding'];
  hoverColor: TrueImpactIconTextProps['hoverColor'];
  onClick?: TrueImpactIconTextProps['onClick'];
}>`
  .icon-text-wrapper {
    display: flex;
    ${({ onClick }) => (onClick ? 'cursor: pointer;' : '')}
    align-items: center;
    justify-content: center;
    gap: ${({ gap }) => gap};
    padding: ${({ padding }) => padding};
    ${({ margin }) => (margin ? `margin: ${margin};` : '')}
    svg path {
      ${({ color }) => (color ? `fill: ${COLORS[color]};` : '')}
    }
    * {
      transition: all 0.3s;
    }
    ${({ hoverColor, onClick }) =>
      hoverColor && onClick
        ? `&:hover { *{color: ${COLORS[hoverColor]} !important;} svg path { fill: ${COLORS[hoverColor]} !important; } }`
        : ''}
    .spinner {
      animation: ${spinAnimation} 1s linear infinite;
    }
  }
`;

const TrueImpactIconText: React.FC<TrueImpactIconTextProps> = ({
  isBold,
  icon,
  label,
  margin,
  variant,
  isSpinner,
  hoverColor = 'brandPrimary',
  color = 'ctaPrimary',
  padding = SPACERS.tiny,
  gap = '6px',
  className,
  allowTextSelection,
  onClick,
}) => {
  return (
    <StyledDiv
      gap={gap}
      color={color}
      margin={margin}
      padding={padding}
      className={className}
      hoverColor={hoverColor}
      onClick={onClick}
    >
      <div className="icon-text-wrapper">
        <FontAwesomeIcon
          icon={icon as IconDefinition | IconProp}
          className={isSpinner ? 'spinner' : undefined}
        />
        <TrueImpactText
          variant={variant || (isBold ? 'body-bold' : undefined)}
          color={color}
          allowTextSelection={allowTextSelection}
        >
          {label}
        </TrueImpactText>
      </div>
    </StyledDiv>
  );
};

export default TrueImpactIconText;
